import React, { useEffect, useState, useMemo, useCallback } from 'react';

import { DrawerComponent } from 'components/Drawer';
import * as S from './styles';
import { AddCircle, ArrowSync } from 'styled-icons/fluentui-system-regular';
import { CollectionIcon } from 'components/Icons';
import { api } from 'services/api';
import loadingSmall from 'assets/image/loading-small.gif';

interface IPropsDataHistory {
  closeDrawer: () => void;
  onClose?: () => void;
}

type IDataHistory = {
  id: string;
  title: string;
  created_at: string;
  description?: string;
  id_log_type: string;
  broker_name?: string;
};

// Mapeamento de tipos de log para ícones
const LOG_TYPE_ICONS = {
  '1': <CollectionIcon />,
  '2': <CollectionIcon />,
  '3': <AddCircle size={24}/>,
  '4': <AddCircle size={24}/>,
  '5': <ArrowSync />,
  '6': <ArrowSync />,
} as const;

// Função para formatar data e hora - mais eficiente
const formatDateTime = (dateString: string) => {
  try {
    const date = new Date(dateString);
    
    // Verifica se a data é válida
    if (isNaN(date.getTime())) {
      return { formattedDate: 'Data inválida', formattedTime: '--:--' };
    }

    // Formato da data: DD/MM/YYYY
    const formattedDate = date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

    // Formato da hora: HH:MM
    const formattedTime = date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    return { formattedDate, formattedTime };
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return { formattedDate: 'Erro', formattedTime: '--:--' };
  }
};

const DataHistory = ({ closeDrawer }: IPropsDataHistory) => {
  const [listHistory, setListHistory] = useState<IDataHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages,setTotalPages] = useState(0)
  const [currentPage, setCurrentPage] = useState<number>(1);
  useEffect(() => {
    const fetchDataHistory = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const accessToken = localStorage.getItem('accessToken');
        const idToken = localStorage.getItem('idToken');

        if (!accessToken || !idToken) {
          throw new Error('Tokens de autenticação não encontrados');
        }

        const { data } = await api.get(`logs?page=${currentPage}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            idToken,
          },
        });
        setTotalPages(data.pagination.pages);
        
        // Se for a primeira página, substitui; senão, acumula
        if (currentPage === 1) {
          setListHistory(data.data || []);
        } else {
          setListHistory(prev => [...prev, ...(data.data || [])]);
        }
      } catch (error) {
        console.error('Erro ao buscar histórico:', error);
        setError('Erro ao carregar histórico');
        setListHistory([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDataHistory();
  }, [currentPage]);

  // Agrupa por data formatada para renderizar cabeçalho por dia
  const groupedHistory = useMemo(() => {
    const groups: Record<string, Array<
      IDataHistory & { formattedDate: string; formattedTime: string; icon: React.ReactNode }
    >> = {};

    for (const item of listHistory) {
      const { formattedDate, formattedTime } = formatDateTime(item.created_at);
      const key = formattedDate;
      const withExtras = {
        ...item,
        formattedDate,
        formattedTime,
        icon: LOG_TYPE_ICONS[item.id_log_type as keyof typeof LOG_TYPE_ICONS] || null,
      };
      if (!groups[key]) groups[key] = [];
      groups[key].push(withExtras);
    }

    return Object.entries(groups).map(([date, items]) => ({ date, items }));
  }, [listHistory]);

  // Refs e callbacks devem estar antes de qualquer early return
  const [nextPageDiv, setNextPageDiv] = useState<HTMLDivElement | null>(null);
  const showMoreHistory = () => {
    setCurrentPage((historyPerPage) => historyPerPage + 1);
  };
  const nextPageDivCallback = useCallback((node: HTMLDivElement | null) => {
    setNextPageDiv(node);
  }, []);
  useEffect(() => {
    if (!nextPageDiv) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && currentPage < totalPages) {
          showMoreHistory();
        }
      },
      {
        root: null,
        rootMargin: '20px',
        threshold: 0.1,
      },
    );

    observer.observe(nextPageDiv);

    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextPageDiv]);
  if (isLoading && currentPage === 1) {
    return (
      <DrawerComponent
        open
        size="default"
        onCompleteClose={closeDrawer}
        titleClose="Histórico"
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
        <img src={loadingSmall} />
        </div>
      </DrawerComponent>
    );
  }

  if (error) {
    return (
      <DrawerComponent
        open
        size="default"
        onCompleteClose={closeDrawer}
        titleClose="Histórico"
      >
        <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
          {error}
        </div>
      </DrawerComponent>
    );
  }

  return (
    <DrawerComponent
      open
      size="default"
      onCompleteClose={closeDrawer}
      titleClose="Histórico"
    >
      {groupedHistory.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
          Nenhum histórico encontrado
        </div>
      ) : (
        <S.ListContainer>
          {groupedHistory.map((group) => (
            <div key={group.date}>
              <S.Description isBold>{group.date}</S.Description>
              {group.items.map((item) => (
                <S.HistoryItem key={item.id}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <S.Broker>{item.formattedTime}</S.Broker>
                    <S.Broker>•</S.Broker>
                    <S.Broker>{item.broker_name || 'N/A'}</S.Broker>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center',
                      marginBottom: item.description ? '8px' : '0px',
                    }}
                  >
                    {item.icon}
                    <S.Description isBold>{item.title}</S.Description>
                  </div>
                  {item.description && (
                    <S.Description>{item.description}</S.Description>
                  )}
                </S.HistoryItem>
              ))}
              <div ref={nextPageDivCallback} />
            </div>  
          ))}
        </S.ListContainer>
      )}
    </DrawerComponent>
  );
};

export default DataHistory;
